import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Brain, Hand, Bone, Footprints, ClipboardList, Stethoscope, ArrowRight } from 'lucide-react';
import { AnimatedSection } from './ui/AnimatedSection';
export function WhatsIncluded() {
  const tomos = [{
    n: '01',
    icon: Brain,
    title: 'Evaluación Clínica',
    problem: 'No sabés por dónde empezar frente al paciente.',
    learn: 'Anamnesis estructurada, inspección, palpación, movilidad y red flags.',
    apply: 'Llegás a la consulta con un protocolo claro y reproducible.'
  }, {
    n: '02',
    icon: Hand,
    title: 'Miembro Superior',
    problem: 'Hombros, codos y muñecas que no sabés cómo abordar.',
    learn: 'Tests específicos, cuadros diferenciales y algoritmos de decisión.',
    apply: 'Aprendé a evaluar hombro, codo y mano con estructura clínica real.'
  }, {
    n: '03',
    icon: Bone,
    title: 'Columna y Raquis',
    problem: 'La columna parece un mundo aparte y siempre te genera dudas.',
    learn: 'Tests neurológicos, clusters y screening de red flags.',
    apply: 'Evaluá cervical, dorsal y lumbar con criterio clínico integrado.'
  }, {
    n: '04',
    icon: Footprints,
    title: 'Miembro Inferior',
    problem: 'Cadera, rodilla y tobillo evaluados de forma desordenada.',
    learn: 'Cadena cinemática, tests específicos y análisis de la marcha.',
    apply: 'Diagnóstico diferencial integrado para todo el miembro inferior.'
  }, {
    n: '05',
    icon: ClipboardList,
    title: 'Pruebas Ortopédicas',
    problem: 'Memorizás tests sin saber cuándo ni para qué usarlos.',
    learn: 'Sensibilidad, especificidad, likelihood ratio y clusters.',
    apply: 'Aplicá pruebas ortopédicas sin memorizar al azar.'
  }, {
    n: '06',
    icon: Stethoscope,
    title: 'Casos Clínicos',
    problem: 'La teoría no te alcanza para resolver casos reales.',
    learn: '15 casos resueltos paso a paso con razonamiento clínico.',
    apply: 'Entrená razonamiento clínico desde casos reales.'
  }];
  return <AnimatedSection id="contenido" className="bg-navy-950 relative" data-id="element-392">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-393">
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-394">
          <div className="inline-block px-3 py-1 rounded-full border border-gold/30 bg-gold/5 text-gold text-[10px] sm:text-xs font-bold tracking-[0.15em] uppercase mb-4 sm:mb-5" data-id="element-395">
            Qué incluye el Mega Pack
          </div>
          <h2 className="font-heading font-extrabold text-2xl sm:text-4xl md:text-6xl text-white leading-[1.05] mb-3 sm:mb-5" data-id="element-396">
            6 tomos. Un sistema clínico{' '}
            <span className="font-editorial italic text-gold font-medium" data-id="element-397">
              completo.
            </span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-400 leading-relaxed px-2" data-id="element-398">
            Cada tomo está pensado para resolver{' '}
            <span className="font-editorial italic text-gold/90" data-id="element-399">
              un problema clínico real.
            </span>{' '}
            No son apuntes sueltos: es una biblioteca organizada para
            acompañarte de la teoría a la práctica.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5" data-id="element-400">
          {tomos.map((tomo, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.05,
          duration: 0.5
        }} className="group relative bg-gradient-to-b from-navy-900 to-navy-950 border border-white/10 hover:border-gold/40 rounded-2xl p-4 sm:p-6 md:p-7 transition-all hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(201,168,76,0.1)]" data-id="element-401">
              <div className="flex items-start justify-between mb-4" data-id="element-402">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center group-hover:bg-gold/20 transition-colors" data-id="element-403">
                  <tomo.icon size={20} className="text-gold" data-id="element-404" />
                </div>
                <span className="font-editorial text-2xl sm:text-3xl md:text-4xl text-white/10 group-hover:text-gold/30 transition-colors leading-none" data-id="element-405">
                  {tomo.n}
                </span>
              </div>

              <h3 className="font-heading font-bold text-base sm:text-lg md:text-xl text-white mb-3 sm:mb-4 leading-tight" data-id="element-406">
                {tomo.title}
              </h3>

              <div className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm" data-id="element-407">
                <div data-id="element-408">
                  <div className="text-gold/70 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest mb-1" data-id="element-409">
                    Problema
                  </div>
                  <p className="text-gray-300 leading-relaxed" data-id="element-410">
                    {tomo.problem}
                  </p>
                </div>
                <div data-id="element-411">
                  <div className="text-gold/70 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest mb-1" data-id="element-412">
                    Aprendés
                  </div>
                  <p className="text-gray-300 leading-relaxed" data-id="element-413">{tomo.learn}</p>
                </div>
                <div className="pt-2.5 sm:pt-3 border-t border-white/5" data-id="element-414">
                  <p className="text-white/95 italic leading-relaxed font-editorial text-sm sm:text-base" data-id="element-415">
                    "{tomo.apply}"
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>

        <div className="text-center mt-8 sm:mt-12" data-id="element-416">
          <a href="#pricing" className="group inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy-950 font-bold text-sm sm:text-base px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all transform hover:scale-[1.02] shadow-[0_0_40px_rgba(201,168,76,0.25)]" data-id="element-417">
            QUIERO EL MEGA PACK COMPLETO
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-418" />
          </a>
        </div>
      </div>
    </AnimatedSection>;
}